import { PageHeader } from '@ant-design/pro-layout'
import type { ViewQueryList } from '@salescore/core'
import { Alert, App, Button, Checkbox, Collapse, Form, type ModalFuncProps, Row } from 'antd'
import { Array, Equivalence, Order, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { hasCustomSearchQuery } from '../../../domain/viewConfig/searchQuery'
import { useSearchableFields } from '../../../recoil/records/selectors/useSearchableFields'
import { changesAtom } from '../../../recoil/recordsHistory'
import { useSetSearchableFields, useViewConfigSheet } from '../../../recoil/viewConfig'
import { flatNodes } from '../../../state/nodeUtility'
import { CustomSearchQueryForm } from './CustomSearchQueryForm'

interface SearchableFieldsFormModalProperties {
  onAfterFinish: () => void
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}

export const buildSearchableFieldsFormModalProperties = (
  properties: SearchableFieldsFormModalProperties,
): ModalFuncProps => ({
  icon: null,
  width: '60%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  title: <PageHeader title={t(`検索項目設定`)} />,
  style: { top: '3%' },
  closable: false,
  maskClosable: true,
  destroyOnClose: true,
  content: <SearchableFieldsFormModalBody {...properties} />,
})

const ignoreMetaTypes = new Set(['url', 'record_url'])

export const SearchableFieldsFormModalBody = ({
  onAfterFinish,
  query,
  setHasChangeToViewSchema,
}: SearchableFieldsFormModalProperties): ReactElement => {
  const changes = useRecoilValue(changesAtom)
  const { message } = App.useApp()

  useEffect(() => {
    if (changes.length > 0) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [changes])

  const flattenNodes = useMemo(() => flatNodes(query.tree), [query])
  const searchableFields = useSearchableFields(query)
  const [pickedFieldNames, setPickedFieldNames] = useState(() => searchableFields.map((x) => x.name))
  const setSearchableFields = useSetSearchableFields()
  const config = useViewConfigSheet()

  const candidateFields = query.fields.filter(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    (x) => x.meta.fieldType === 'string' && !ignoreMetaTypes.has(x.meta.fieldMetaType as string),
  )
  const stringFieldsWithNode = pipe(
    flattenNodes,
    Array.map((node) => ({
      node,
      fields: candidateFields.filter((field) => Array.makeEquivalence(Equivalence.String)(field.nodePath, node.path)),
    })),
    Array.filter((x) => x.fields.length > 0),
    Array.sortBy(Order.mapInput(Order.Number, (x) => x.node.path.length)),
  )
  const hasCustomQuery = hasCustomSearchQuery(config.meta?.searchQuery)
  const hasNoPickedFields = pickedFieldNames.length === 0

  return (
    <>
      {hasCustomQuery ? (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message={t(
            '検索条件設定が有効なため、通常設定は無効化されています。解除する場合は「高度な設定」から検索条件をリセットしてください。',
          )}
        />
      ) : null}
      <Form
        layout="vertical"
        onFinish={() => {
          setSearchableFields({
            searchableFields: pickedFieldNames
              .map((name) => query.fields.find((field) => field.name === name))
              .filter(isNotNullish),
            onSetConfig: () => {
              setHasChangeToViewSchema(true)
            },
          })
          onAfterFinish()
        }}
      >
        {hasCustomQuery ? null : (
          <div className="mb-4">
            {t(`検索対象としたいフィールドを選択してください。`)}
            <br />
            <Alert
              className="mt-2"
              showIcon
              type="warning"
              message={
                <span className="text-xs">
                  ※{t(`検索対象となるフィールドが多ければ多いほど、検索に時間がかかります。`)}
                </span>
              }
            />
          </div>
        )}
        {!hasCustomQuery && hasNoPickedFields ? (
          <Alert
            className="mb-4"
            type="warning"
            showIcon
            message={t('検索対象の項目が選択されていません。フリーワード検索は使用できません。')}
          />
        ) : null}
        {stringFieldsWithNode.map(({ node, fields }, index) => (
          <Form.Item key={`${node.name}_${index}`} label={node.meta.label}>
            <Checkbox.Group
              disabled={hasCustomQuery}
              defaultValue={pickedFieldNames}
              onChange={(newFieldNames) => {
                setPickedFieldNames((oldFieldNames) => [
                  // eslint-disable-next-line max-nested-callbacks,sonarjs/no-nested-functions
                  ...oldFieldNames.filter((oldFieldName) => !fields.map((x) => x.name).includes(oldFieldName)),
                  ...newFieldNames,
                ])
              }}
              options={fields.map((field) => ({
                label: field.meta.label,
                value: field.name,
              }))}
            />
          </Form.Item>
        ))}
        {hasCustomQuery ? null : (
          <Row justify="end">
            <Button htmlType="submit">{t(`適用`)}</Button>
          </Row>
        )}
      </Form>
      <Collapse
        // 検索条件の有無で作り直し、リセット後を「未設定で開いたとき」と同じ初期状態にする。
        // 作り直しなので閉じる動きは出ず、エディタも作り直されて例文 (defaultValue) が入る。
        key={hasCustomQuery ? 'custom' : 'none'}
        defaultActiveKey={hasCustomQuery ? ['custom'] : []}
        className="my-4"
      >
        <Collapse.Panel header={t(`高度な設定`)} key="custom">
          <CustomSearchQueryForm onAfterFinish={onAfterFinish} setHasChangeToViewSchema={setHasChangeToViewSchema} />
        </Collapse.Panel>
      </Collapse>
    </>
  )
}
